import React, { useState } from 'react';
import {
  PlusIcon,
  WrenchScrewdriverIcon,
  UsersIcon,
  TrashIcon,
  UserGroupIcon,
  Cog6ToothIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  PencilIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { parseRiggingConfig } from '../../utils/riggingValidation';
import { getSidePreferenceForBoatType } from '../../utils/boatTypes';

const Crews = ({ crews, handleCreateCrew, deleteCrew, onEditCrew }) => {
  const [expandedCrews, setExpandedCrews] = useState(new Set());

  const toggleCrewExpansion = (crewId) => {
    const newExpanded = new Set(expandedCrews);
    if (newExpanded.has(crewId)) {
      newExpanded.delete(crewId);
    } else {
      newExpanded.add(crewId);
    }
    setExpandedCrews(newExpanded);
  };

  // Check if a crew needs rigging configuration
  const needsRiggingConfig = (crew) => {
    if (!crew.boat_type) return false;
    const sidePreference = getSidePreferenceForBoatType(crew.boat_type);
    return sidePreference?.isStrict && !crew.rigging_config;
  };

  const renderRiggingConfig = (riggingConfig, crewId) => {
    if (!riggingConfig) return null;

    const parsed = parseRiggingConfig(riggingConfig);
    if (!parsed || Object.keys(parsed).length === 0) return null;

    const seatEntries = Object.entries(parsed)
      .sort(([a], [b]) => parseInt(a) - parseInt(b))
      .map(([seat, side]) => ({ seat: parseInt(seat), side }));

    const isExpanded = expandedCrews.has(crewId);

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Cog6ToothIcon className="h-3 w-3" />
            <span>Rigging:</span>
            <span className="font-medium text-green-600">
              Configured ({seatEntries.length} seats)
            </span>
          </div>
          <button
            onClick={() => toggleCrewExpansion(crewId)}
            className="flex items-center gap-1 text-xs text-blue-600 transition-colors hover:text-blue-700"
          >
            {isExpanded ? (
              <>
                <ChevronUpIcon className="h-3 w-3" />
                Hide
              </>
            ) : (
              <>
                <ChevronDownIcon className="h-3 w-3" />
                Show
              </>
            )}
          </button>
        </div>
        {isExpanded && (
          <div className="mt-2 rounded-md border border-gray-200 bg-gray-50 p-3">
            <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-3 lg:grid-cols-4">
              {seatEntries.map(({ seat, side }) => (
                <div
                  key={seat}
                  className={`flex items-center justify-between rounded p-2 ${
                    side === 'port'
                      ? 'border border-red-200 bg-red-50'
                      : side === 'starboard'
                        ? 'border border-green-200 bg-green-50'
                        : 'border border-gray-200 bg-gray-50'
                  }`}
                >
                  <span className="font-medium">{seat}</span>
                  <span
                    className={`rounded px-2 py-1 text-xs font-medium ${
                      side === 'port'
                        ? 'bg-red-100 text-red-800'
                        : side === 'starboard'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {side}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Crews</h2>
          <p className="mt-1 text-sm text-gray-600">Manage crews and their configurations</p>
        </div>
        <button
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:w-auto"
          onClick={handleCreateCrew}
        >
          <PlusIcon className="h-4 w-4" />
          Create Crew
        </button>
      </div>
      {crews.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-gray-300 p-8 text-center">
          <WrenchScrewdriverIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No crews</h3>
          <p className="mt-1 text-sm text-gray-500">Get started by creating a new crew.</p>
        </div>
      ) : (
        <div className="admin-grid grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {crews.map((c) => (
            <div
              key={c.id}
              className="group relative overflow-hidden rounded-lg border border-gray-200 bg-white p-5 shadow-sm transition-all hover:shadow-md"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  {needsRiggingConfig(c) && (
                    <div className="mb-2 rounded-md border border-amber-200 bg-amber-50 p-2">
                      <div className="flex items-center gap-2 text-xs text-amber-800">
                        <ExclamationTriangleIcon className="h-4 w-4" />
                        <span className="font-medium">Rigging configuration missing</span>
                      </div>
                      <p className="mt-1 text-xs text-amber-700">
                        This {c.boat_type} requires side assignments for each seat.
                      </p>
                    </div>
                  )}
                  <div className="mb-2 flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-gray-900">{c.name}</h3>
                    {c.is_experimental && (
                      <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">
                        Experimental
                      </span>
                    )}
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <WrenchScrewdriverIcon className="h-4 w-4" />
                      <span className="font-medium">{c.boat_type}</span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <UsersIcon className="h-3 w-3" />
                          {c.member_count || 0} rowers
                        </span>
                      </div>
                      {c.rigging_config && renderRiggingConfig(c.rigging_config, c.id)}
                      {needsRiggingConfig(c) && (
                        <div className="flex items-center gap-2 text-xs text-amber-600">
                          <ExclamationTriangleIcon className="h-4 w-4" />
                          <span>Rigging configuration required</span>
                        </div>
                      )}
                    </div>
                    {c.training_group && (
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <UserGroupIcon className="h-4 w-4" />
                        <span>{c.training_group.name}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-4 flex flex-col items-start justify-between gap-3 border-t border-gray-100 pt-3 sm:flex-row sm:items-center">
                <div className="text-xs text-gray-500">ID: {c.id}</div>
                <div className="mobile-button-group flex w-full justify-end gap-2 sm:w-auto">
                  <button
                    className="flex items-center gap-2 rounded-md bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    onClick={() => onEditCrew(c)}
                    title="Edit crew"
                  >
                    <PencilIcon className="h-4 w-4" />
                    <span className="sm:hidden">Edit</span>
                  </button>
                  <button
                    className="flex items-center gap-2 rounded-md bg-red-50 px-3 py-1.5 text-sm font-medium text-red-700 transition-colors hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                    onClick={() => deleteCrew(c)}
                  >
                    <TrashIcon className="h-4 w-4" />
                    <span className="sm:hidden">Delete</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default Crews;
